"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { useHideAiApplyForAbroad } from "@/components/feed/abroad-view-context";
import { useHydrated } from "@/lib/hydration/use-hydrated";
import { shouldHideAiApplyBadge } from "@/lib/match/low-fit";
import { useJobContext } from "./job-context";

type Surface = "feed_card" | "job_detail";

type Props = {
	/**
	 * The supported-ATS platform bucket (e.g. `"greenhouse"`, `"lever"`) from
	 * {@link detectAutofillSupportedAts}, or `null` when the job is NOT on an
	 * autofill-supported ATS. Render nothing when `null` — the badge is a
	 * positive signal, never a "not supported" marker.
	 */
	ats: string | null;
	surface: Surface;
	jobId: string;
	locale: string;
};

/**
 * "AI-powered apply" badge — surfaced on jobs whose apply destination lands on
 * an ATS the browser extension's Phase-2 autofill adapters fill in ~1 click
 * (Greenhouse / Lever / Ashby / Workday / Workable / SmartRecruiters / iCIMS /
 * BambooHR / Taleo / Jobvite / Contra). It makes the killer "30 min → 5 min"
 * promise *visible* at the moment of intent.
 *
 * Gating: the new `ai-apply-badge` PostHog flag is the SOLE switch (default OFF
 * ⇒ `useFeatureFlagEnabled` resolves undefined/false ⇒ inert). No hardcoded
 * constant shadows it. Client-side eval keeps the job-detail page ISR/edge-safe
 * — the badge only paints after hydration when the flag is on.
 *
 * Telemetry: fires `ai_apply_badge_shown` (impression denominator) and
 * `ai_apply_badge_clicked` on click — so the founder can read whether the
 * promise actually pulls applies, broken out by ATS + surface. On the feed
 * the impression is de-duped to once per (pathname, ats): the per-mount
 * version fired 20–35 near-identical events on every feed pageview (one per
 * card — the 2026-07-14 audit's event-spam finding) while nothing consumes
 * per-job impressions; clicks still carry the job_id.
 */

/** Feed-card impressions already reported this page load, keyed `${pathname}|${ats}`. */
const feedImpressionFired = new Set<string>();

export function AiApplyBadge({ ats, surface, jobId, locale }: Props) {
	// `useHydrated()` gates the RENDER, not just the effect. posthog-js/react
	// reads the flag synchronously in a `useState` initializer from
	// localStorage-persisted flags, and this component renders inside the feed's
	// streamed <Suspense> boundary (jobs/page.tsx), which hydrates AFTER
	// PostHogProvider's init effect. Without the gate a returning visitor gets
	// `enabled === true` on the boundary's FIRST client render while the server
	// HTML has no badge — React 19 throws #418 and discards the entire feed
	// subtree (~40 cards, pagination, sidebar, JSON-LD), which is where ~10% of
	// feed pageviews were landing. Do NOT replace this with `typeof window`:
	// that is already true during the hydration render and fixes nothing.
	const hydrated = useHydrated();
	const enabled = useFeatureFlagEnabled("ai-apply-badge");
	// On the job page the actionable "Lamar dengan AI" button
	// (`AiApplyJobButton`, flag `ai-apply-everywhere`) replaces this badge.
	const replacedByButton =
		useFeatureFlagEnabled("ai-apply-everywhere") === true &&
		surface === "job_detail";
	const posthog = usePostHog();
	const shownRef = useRef(false);
	// A "1-click apply" promise on a job the scorer already bucketed `low` is an
	// invitation to a wasted application (2026-09-03 audit: 0 of 5 required
	// skills matched, badge shown, application sent). Outside the job-context
	// provider (feed cards) the hook returns the guest default and nothing hides.
	const { fitScoreBreakdown } = useJobContext();
	const lowFit = shouldHideAiApplyBadge(surface, fitScoreBreakdown);

	// Abroad view: hidden for visitors in (or of unknown location in) Indonesia.
	const hiddenForAbroad = useHideAiApplyForAbroad();

	const visible =
		hydrated &&
		!hiddenForAbroad &&
		enabled === true &&
		!replacedByButton &&
		ats !== null &&
		!lowFit;

	useEffect(() => {
		if (!visible || shownRef.current) return;
		shownRef.current = true;
		if (surface === "feed_card") {
			const key = `${window.location.pathname}|${ats}`;
			if (feedImpressionFired.has(key)) return;
			feedImpressionFired.add(key);
		}
		posthog?.capture("ai_apply_badge_shown", {
			// Feed impressions are page-level (see de-dupe above) — a single
			// card's id would be misleading there.
			job_id: surface === "feed_card" ? null : jobId,
			ats,
			surface,
			locale,
		});
	}, [visible, jobId, ats, surface, locale, posthog]);

	if (!visible) return null;

	const label =
		locale === "id" ? "Formulir terisi otomatis" : "Form fills itself";
	const tooltip =
		locale === "id"
			? "Ekstensi gratis kami mengisi formulir lamaran ini untukmu di Chrome, Edge atau Brave. Kamu tetap menekan Kirim sendiri."
			: "Our free extension fills this application form for you in Chrome, Edge or Brave. You still click Submit yourself.";

	return (
		<button
			type="button"
			title={tooltip}
			aria-label={`${label} — ${tooltip}`}
			onClick={() => {
				posthog?.capture("ai_apply_badge_clicked", {
					job_id: jobId,
					ats,
					surface,
					locale,
				});
			}}
			data-testid="ai-apply-badge"
			data-ats={ats}
			className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary transition-colors hover:border-primary/60 hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
		>
			{label}
		</button>
	);
}
